import { useEffect, useRef, useState } from 'preact/hooks';
import { emptyRecipe, type RecipeData } from '../../shared/recipe';
import { RecipeForm } from '../components/RecipeForm';
import { TagInput } from '../components/TagInput';
import { ErrorBox, Loading, Modal, Segmented, StarInput } from '../components/ui';
import { api, ApiError, qs, type Card, type CardSummary, type CardType, type Page } from '../lib/api';
import { diffSnapshots } from '../lib/diff';
import { renderMarkdown } from '../lib/markdown';
import { navigate } from '../lib/router';
import { errorText, toast, useAsync } from '../lib/store';

interface Form {
  type: string;
  title: string;
  summary: string;
  body_md: string;
  tags: string[];
  status: string;
  rating: number | null;
  favourite: boolean;
  source_url: string;
  source_note: string;
  data: Record<string, any>;
}

interface LocalDraft {
  form: Form;
  baseVersion: number | null;
  savedAt: string;
}

const draftKey = (id: string | undefined, type: string | undefined) => `bakery.draft.${id ?? `new-${type}`}`;

function fromCard(c: Card): Form {
  return {
    type: c.type,
    title: c.title,
    summary: c.summary ?? '',
    body_md: c.body_md ?? '',
    tags: c.tags,
    status: c.status,
    rating: c.rating,
    favourite: c.favourite,
    source_url: c.source_url ?? '',
    source_note: c.source_note ?? '',
    data: c.data,
  };
}

function blankForm(type: string): Form {
  return { type, title: '', summary: '', body_md: '', tags: [], status: 'active', rating: null, favourite: false, source_url: '', source_note: '', data: type === 'recipe' ? emptyRecipe() : {} };
}

function toPayload(f: Form) {
  return {
    title: f.title.trim(),
    summary: f.summary.trim() || null,
    body_md: f.body_md.trim() || null,
    tags: f.tags,
    status: f.status,
    rating: f.rating,
    favourite: f.favourite,
    source_url: f.source_url.trim() || null,
    source_note: f.source_note.trim() || null,
    data: cleanData(f.data),
  };
}

/** Drops blank ingredient rows/steps the user left behind. */
function cleanData(d: Record<string, any>) {
  if (!d.ingredient_groups) return d;
  return {
    ...d,
    ingredient_groups: d.ingredient_groups
      .map((g: any) => ({ ...g, items: g.items.filter((i: any) => i.item?.trim()) }))
      .filter((g: any, i: number, all: any[]) => g.items.length || g.name || all.length === 1),
    steps: (d.steps ?? []).filter((s: any) => s.text?.trim()).map((s: any, i: number) => ({ ...s, n: i + 1 })),
  };
}

function readDraft(key: string): LocalDraft | null {
  try {
    return JSON.parse(localStorage.getItem(key) ?? 'null');
  } catch {
    return null;
  }
}

/** Generic schema-driven fields for types without a dedicated form. */
function SchemaFields({ schema, data, onChange }: { schema: any; data: Record<string, any>; onChange: (d: Record<string, any>) => void }) {
  const props = Object.entries<any>(schema?.properties ?? {});
  if (!props.length) return null;
  return (
    <fieldset class="panel field-grid">
      <legend>Details</legend>
      {props.map(([k, p]) => {
        const label = p.title ?? k.replace(/_/g, ' ');
        const types = [p.type, ...(p.anyOf ?? []).map((x: any) => x.type)].flat();
        if (types.includes('boolean'))
          return (
            <label class="checkbox">
              <input type="checkbox" checked={!!data[k]} onChange={(e) => onChange({ ...data, [k]: e.currentTarget.checked })} /> {label}
            </label>
          );
        if (types.includes('array'))
          return (
            <div>
              <label for={`sf-${k}`}>{label}</label>
              <input id={`sf-${k}`} class="dr-input" placeholder="comma separated" value={(data[k] ?? []).join(', ')} onChange={(e) => onChange({ ...data, [k]: e.currentTarget.value.split(',').map((s) => s.trim()).filter(Boolean) })} />
            </div>
          );
        if (p.enum)
          return (
            <div>
              <label for={`sf-${k}`}>{label}</label>
              <select id={`sf-${k}`} class="dr-input" value={data[k] ?? ''} onChange={(e) => onChange({ ...data, [k]: e.currentTarget.value || null })}>
                <option value="">—</option>
                {p.enum.map((v: string) => (
                  <option value={v}>{v}</option>
                ))}
              </select>
            </div>
          );
        const numeric = types.includes('number') || types.includes('integer');
        return (
          <div>
            <label for={`sf-${k}`}>{label}</label>
            <input
              id={`sf-${k}`}
              class="dr-input"
              inputMode={numeric ? 'decimal' : undefined}
              value={data[k] ?? ''}
              onChange={(e) => {
                const v = e.currentTarget.value;
                onChange({ ...data, [k]: v === '' ? null : numeric ? Number(v) : v });
              }}
            />
          </div>
        );
      })}
    </fieldset>
  );
}

function ConflictDialog({ mine, theirs, onKeepMine, onUseTheirs, onClose }: { mine: Form; theirs: Card; onKeepMine: () => void; onUseTheirs: () => void; onClose: () => void }) {
  const d = diffSnapshots({ ...theirs, data: theirs.data }, { ...toPayload(mine), data: cleanData(mine.data) });
  return (
    <Modal title="This card changed while you were editing" onClose={onClose} wide>
      <p>
        Someone ({theirs.updated_by === 'user' ? 'you, elsewhere' : theirs.updated_by.replace('client:', '')}) saved version {theirs.version}. Here is how your edit differs from it:
      </p>
      {!d.fields.length && !d.steps.length && !d.ingredients.length && <p class="notice">Your edit matches the saved version — saving just records it as a new version.</p>}
      <table class="diff" hidden={!d.fields.length && !d.steps.length && !d.ingredients.length}>
        <thead>
          <tr>
            <th>Field</th>
            <th>Saved (v{theirs.version})</th>
            <th>Yours</th>
          </tr>
        </thead>
        <tbody>
          {[...d.fields, ...d.steps].map((f) => (
            <tr>
              <td>{f.field}</td>
              <td class="before">{f.before}</td>
              <td class="after">{f.after}</td>
            </tr>
          ))}
          {d.ingredients.map((x) => (
            <tr>
              <td>Ingredient {x.kind === 'added' ? '(only yours)' : x.kind === 'removed' ? '(only saved)' : ''}</td>
              <td class="before">{x.before ?? ''}</td>
              <td class="after">{x.after ?? ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div class="toolbar">
        <button class="dr-btn dr-btn--primary" onClick={onKeepMine}>
          Save mine as v{theirs.version + 1}
        </button>
        <button class="dr-btn" onClick={onUseTheirs}>
          Discard mine, load v{theirs.version}
        </button>
        <button class="dr-btn bk-ghost" onClick={onClose}>
          Keep editing
        </button>
      </div>
    </Modal>
  );
}

export function Editor({ id, type }: { id?: string; type?: string }) {
  const existing = useAsync(() => (id ? api.get<Card>(`/cards/${id}`) : Promise.resolve(null)), [id]);
  const types = useAsync(() => api.get<{ items: CardType[] }>('/types'), []);
  const recipes = useAsync(() => api.get<Page<CardSummary>>(`/cards${qs({ type: 'recipe', limit: 100, sort: 'title' })}`), []);
  const [form, setForm] = useState<Form | null>(null);
  const [baseVersion, setBaseVersion] = useState<number | null>(null);
  const [changeNote, setChangeNote] = useState('');
  const [preview, setPreview] = useState<'write' | 'preview'>('write');
  const [restorable, setRestorable] = useState<LocalDraft | null>(null);
  const [conflict, setConflict] = useState<Card | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cover, setCover] = useState<File | null>(null);
  const key = draftKey(id, type);
  const dirty = useRef(false);

  useEffect(() => {
    if (id && !existing.data) return;
    const initial = existing.data ? fromCard(existing.data) : blankForm(type ?? 'note');
    setForm(initial);
    setBaseVersion(existing.data?.version ?? null);
    const d = readDraft(key);
    if (d && JSON.stringify(d.form) !== JSON.stringify(initial)) setRestorable(d);
  }, [existing.data, type]);

  // Autosave to a local draft (debounced). Explicit Save creates the revision.
  useEffect(() => {
    if (!form || !dirty.current) return;
    const t = setTimeout(() => {
      try {
        localStorage.setItem(key, JSON.stringify({ form, baseVersion, savedAt: new Date().toISOString() } satisfies LocalDraft));
      } catch {
        /* storage full / private mode */
      }
    }, 600);
    return () => clearTimeout(t);
  }, [form]);

  useEffect(() => {
    const h = (e: BeforeUnloadEvent) => {
      if (dirty.current) e.preventDefault();
    };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, []);

  if (existing.error) return <ErrorBox error={existing.error} />;
  if (!form) return <Loading />;

  const update = (patch: Partial<Form>) => {
    dirty.current = true;
    setForm({ ...form, ...patch });
  };
  const clearDraft = () => {
    dirty.current = false;
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  };

  const save = async (opts: { version?: number } = {}) => {
    setSaving(true);
    setError(null);
    try {
      let card: Card;
      if (!id) {
        const res = await api.post<{ card: Card; warnings: string[] }>('/cards', { type: form.type, ...toPayload(form), change_note: changeNote || 'Created' }, { 'idempotency-key': crypto.randomUUID() });
        card = res.card;
        res.warnings.forEach((w) => toast(w, 'error'));
      } else {
        const v = opts.version ?? baseVersion!;
        const res = await api.patch<{ card: Card; warnings: string[] }>(`/cards/${id}`, { ...toPayload(form), change_note: changeNote || null }, { 'if-match': `"${v}"` });
        card = res.card;
        res.warnings.forEach((w) => toast(w, 'error'));
      }
      if (cover) {
        const fd = new FormData();
        fd.append('role', 'cover');
        fd.append('file', cover);
        await api.upload(`/cards/${card.id}/media`, fd);
      }
      clearDraft();
      toast(id ? `Saved v${card.version}` : 'Created');
      navigate(`/c/${card.id}/${card.slug}`);
    } catch (e) {
      if (e instanceof ApiError && e.status === 412 && id) setConflict(await api.get<Card>(`/cards/${id}`));
      else setError(errorText(e) + (e instanceof ApiError && e.body?.issues ? `\n${e.body.issues.map((i: any) => `• ${i.path}: ${i.message}`).join('\n')}` : ''));
    } finally {
      setSaving(false);
    }
  };

  const typeDef = types.data?.items.find((t) => t.key === form.type);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <h1>{id ? `Edit ${existing.data?.title}` : `New ${typeDef?.label.toLowerCase() ?? form.type}`}</h1>
      {restorable && (
        <div class="notice notice-warning row-between" style={{ marginBottom: 'var(--bk-space-4)' }}>
          <span>You have an unsaved draft from {new Date(restorable.savedAt).toLocaleString()}.</span>
          <div class="row">
            <button
              type="button"
              class="dr-btn bk-sm"
              onClick={() => {
                setForm(restorable.form);
                dirty.current = true;
                setRestorable(null);
              }}
            >
              Restore draft
            </button>
            <button
              type="button"
              class="dr-btn bk-ghost bk-sm"
              onClick={() => {
                clearDraft();
                setRestorable(null);
              }}
            >
              Discard
            </button>
          </div>
        </div>
      )}
      {existing.data?.needs_review && <p class="notice notice-warning">This card is awaiting your review. Saving does not clear the flag — use “Mark reviewed” on the card.</p>}

      <div class="stack">
        <fieldset class="panel">
          <legend>Card</legend>
          <div class="field">
            <label for="title">Title</label>
            <input id="title" class="dr-input" value={form.title} onInput={(e) => update({ title: e.currentTarget.value })} required maxLength={200} />
          </div>
          <div class="field">
            <label for="summary">Summary (one line for the card face)</label>
            <input id="summary" class="dr-input" value={form.summary} onInput={(e) => update({ summary: e.currentTarget.value })} maxLength={500} />
          </div>
          <div class="field">
            <label for="tags">Tags</label>
            <TagInput id="tags" value={form.tags} onChange={(tags) => update({ tags })} />
          </div>
          <div class="field-grid">
            <div>
              <label for="status">Status</label>
              <select id="status" class="dr-input" value={form.status} onChange={(e) => update({ status: e.currentTarget.value })}>
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <div>
              <label>Rating</label>
              <StarInput value={form.rating} onChange={(rating) => update({ rating })} />
            </div>
            <label class="checkbox">
              <input type="checkbox" checked={form.favourite} onChange={(e) => update({ favourite: e.currentTarget.checked })} /> Favourite
            </label>
          </div>
          <div class="field-grid">
            <div>
              <label for="src">Source URL</label>
              <input id="src" class="dr-input" type="url" value={form.source_url} onInput={(e) => update({ source_url: e.currentTarget.value })} />
            </div>
            <div>
              <label for="srcn">Source note</label>
              <input id="srcn" class="dr-input" placeholder="Ottolenghi – Simple p.112" value={form.source_note} onInput={(e) => update({ source_note: e.currentTarget.value })} />
            </div>
            <div>
              <label for="cover">{existing.data?.cover ? 'Replace cover image' : 'Cover image'}</label>
              <input id="cover" class="dr-input" type="file" accept="image/*" onChange={(e) => setCover(e.currentTarget.files?.[0] ?? null)} />
            </div>
          </div>
        </fieldset>

        {form.type === 'recipe' ? (
          <RecipeForm data={form.data as RecipeData} onChange={(data) => update({ data })} recipes={(recipes.data?.items ?? []).filter((r) => r.id !== id)} />
        ) : (
          <SchemaFields schema={typeDef?.json_schema} data={form.data} onChange={(data) => update({ data })} />
        )}

        <fieldset class="panel">
          <legend>{form.type === 'recipe' ? 'Notes, variations, story' : 'Content'} (Markdown)</legend>
          <Segmented
            label="Editor mode"
            value={preview}
            onChange={setPreview}
            options={[
              ['write', 'Write'],
              ['preview', 'Preview'],
            ]}
          />
          {preview === 'write' ? (
            <textarea class="dr-input" aria-label="Markdown body" style={{ marginTop: 'var(--bk-space-2)', minHeight: 'var(--textarea-tall)' }} value={form.body_md} onInput={(e) => update({ body_md: e.currentTarget.value })} />
          ) : (
            <div class="prose panel" style={{ marginTop: 'var(--bk-space-2)' }} dangerouslySetInnerHTML={{ __html: renderMarkdown(form.body_md) || '<p class="muted">Nothing yet.</p>' }} />
          )}
        </fieldset>
      </div>

      {error && (
        <p class="error" role="alert" style={{ whiteSpace: 'pre-wrap', marginTop: 'var(--bk-space-4)' }}>
          {error}
        </p>
      )}
      <div class="sticky-actions">
        <input class="dr-input grow" aria-label="Change note" placeholder="Change note (optional), e.g. “Doubled the garlic”" value={changeNote} onInput={(e) => setChangeNote(e.currentTarget.value)} />
        <button class="dr-btn dr-btn--primary" disabled={saving}>
          {saving ? 'Saving…' : id ? 'Save' : 'Create'}
        </button>
        <button
          type="button"
          class="dr-btn bk-ghost"
          onClick={() => {
            if (!dirty.current || confirm('Discard your changes? (Your local draft is kept.)')) history.back();
          }}
        >
          Cancel
        </button>
      </div>

      {conflict && (
        <ConflictDialog
          mine={form}
          theirs={conflict}
          onClose={() => setConflict(null)}
          onKeepMine={() => {
            const v = conflict.version;
            setConflict(null);
            setBaseVersion(v);
            void save({ version: v });
          }}
          onUseTheirs={() => {
            clearDraft();
            setForm(fromCard(conflict));
            setBaseVersion(conflict.version);
            setConflict(null);
          }}
        />
      )}
    </form>
  );
}
